import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Checks({ label, opts, value, onChange, testId }) {
  const set = new Set(value || []);
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold text-slate-600 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {opts.map((o) => (
          <button key={o.key} type="button" aria-pressed={set.has(o.key)} data-testid={`${testId}-${o.key}`} onClick={() => { const n = new Set(set); n.has(o.key) ? n.delete(o.key) : n.add(o.key); onChange([...n]); }}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${set.has(o.key) ? "border-brand bg-brand text-white" : "border-slate-200 text-slate-600 hover:border-brand/50"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

/** Edit Email Center targeting tags on an existing template (one template can carry many tags). */
export function TargetingDialog({ template, taxonomy, onClose, onSaved }) {
  const [tg, setTg] = useState({ purposes: [], relationships: [], backgrounds: [], familiarity: [], programs: [], stages: [], ...(template.targeting || {}) });
  const set = (k) => (v) => setTg((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try { await api.patch(`/email-center/templates/${template.id}/targeting`, tg); toast.success("Template tags saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const subs = taxonomy.purposes.flatMap((g) => g.items.map((i) => ({ key: i.key, label: `${g.label}: ${i.label}` })));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl" data-testid="targeting-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Email Center tags · {template.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input placeholder="Intended audience" value={tg.audience || ""} onChange={(e) => set("audience")(e.target.value)} data-testid="tg-audience" />
            <Input placeholder="Recommended use case" value={tg.use_case || ""} onChange={(e) => set("use_case")(e.target.value)} data-testid="tg-use-case" />
            <Input placeholder="Tone (e.g. warm, formal)" value={tg.tone || ""} onChange={(e) => set("tone")(e.target.value)} data-testid="tg-tone" />
          </div>
          <Checks label="Purposes" opts={subs} value={tg.purposes} onChange={set("purposes")} testId="tg-purposes" />
          <Checks label="Relationship history" opts={taxonomy.relationships} value={tg.relationships} onChange={set("relationships")} testId="tg-relationships" />
          <Checks label="Professional background" opts={taxonomy.backgrounds} value={tg.backgrounds} onChange={set("backgrounds")} testId="tg-backgrounds" />
          <Checks label="Familiarity" opts={taxonomy.familiarity} value={tg.familiarity} onChange={set("familiarity")} testId="tg-familiarity" />
          <Checks label="Programs" opts={taxonomy.programs} value={tg.programs} onChange={set("programs")} testId="tg-programs" />
          <Checks label="Pipeline stages (optional)" opts={taxonomy.stages.map((s) => ({ key: s, label: s }))} value={tg.stages} onChange={set("stages")} testId="tg-stages" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!tg.featured} onChange={(e) => set("featured")(e.target.checked)} data-testid="tg-featured" />Featured / preferred</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-brand hover:bg-brand-strong" data-testid="tg-save">Save tags</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
